<template>
    <div class="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-x-10">
        <div v-for="(row, i) in rows" :key="i"
            class="flex items-baseline justify-between gap-4 py-2.5 border-b border-line-soft">
            <span class="flex items-center gap-1 min-w-0">
                <span class="text-[13px] text-muted truncate">{{ $t(row.label) }}</span>
                <InfoTip v-if="row.tip" :title="$t(`snap-${row.tip}-title`)" :text="$t(`snap-${row.tip}`)" />
            </span>
            <span class="qp-num text-[13px] font-medium text-right whitespace-nowrap" :class="toneClass(row)">
                <template v-if="row.kind === 'bool'">
                    <span v-if="row.raw === true || row.raw === false" class="qp-chip"
                        :class="row.raw ? 'qp-chip-up' : ''">{{ row.raw ? $t('Yes') : $t('No') }}</span>
                    <span v-else>{{ dash }}</span>
                </template>
                <template v-else>
                    {{ row.value }}<span v-if="row.hint" class="text-[11px] font-normal text-faint ml-1">{{ row.hint }}</span>
                </template>
            </span>
        </div>
    </div>
</template>

<script>
import { DASH, isNum, money, num, pct, compact, signed, tone } from '@/utils/format'
import InfoTip from './InfoTip.vue'

// kind -> formatter. "tone" kinds are coloured green/red by sign.
const FORMAT = {
    money: (v) => money(v),
    num: (v) => num(v),
    int: (v) => num(v, 0),
    pct: (v) => pct(v),
    pctTone: (v) => pct(v, 2, { signed: true }),
    compact: (v) => compact(v),
    moneyTone: (v) => signed(v),
}

export default {
    name: 'StockTable',
    components: { InfoTip },
    props: {
        metricData: { type: Object, default: () => ({}) },
        priceMetricsData: { type: Object, default: () => ({}) },
        companyProfileData: { type: Object, default: () => ({}) },
        price: { type: Object, default: () => ({}) },
        extraData: { type: Object, default: () => ({}) },
    },
    data() {
        return { dash: DASH }
    },
    computed: {
        rows() {
            const m = this.metricData || {}
            const p = this.priceMetricsData || {}
            const c = this.companyProfileData || {}
            const q = this.price || {}
            const x = this.extraData || {}

            // Price, day/52-week ranges, volume and market cap are in the header;
            // dividends and the next earnings date have their own cards.
            const cashPerShare = Number(m.cashPerSharePerShareQuarterly)
            const priceToCash = isNum(q.currentPrice) && cashPerShare > 0 ? Number(q.currentPrice) / cashPerShare : null
            const list = [
                // Technicals
                { label: 'ATR (14)', tip: 'atr', kind: 'money', raw: x.atr },
                { label: 'RSI (14)', tip: 'rsi', kind: 'num', raw: p._14DayRSI },
                { label: 'SMA30', tip: 'sma30', kind: 'money', raw: p._30DaySMA },
                { label: 'SMA50', tip: 'sma50', kind: 'money', raw: p._50DaySMA },
                { label: 'SMA200', tip: 'sma200', kind: 'money', raw: p._200DaySMA },
                { label: 'Beta', tip: 'beta', kind: 'num', raw: m.beta },
                // 30-day historical volatility, annualised, as a fraction (0.305 = 30.5%).
                { label: 'Volatility (30D)', tip: 'volatility', kind: 'pct', raw: isNum(x.volatility) ? Number(x.volatility) * 100 : null },
                { label: 'Weekly Optionable', tip: 'weekly', kind: 'bool', raw: x.weeklyOptionable },

                // Performance
                { label: 'Perf Month', tip: 'perf-month', kind: 'pctTone', raw: p._1MonthPriceReturn },
                { label: 'Perf Quarter', tip: 'perf-quarter', kind: 'pctTone', raw: p._3MonthPriceReturn },
                { label: 'Perf Half Year', tip: 'perf-half', kind: 'pctTone', raw: p._6MonthPriceReturn },
                { label: 'Perf Year', tip: 'perf-year', kind: 'pctTone', raw: p._12MonthPriceReturn },
                { label: 'Perf YTD', tip: 'perf-ytd', kind: 'pctTone', raw: p.ytdPriceReturn },

                // Valuation
                // Finnhub reports shares outstanding in millions.
                { label: 'Shares Outstanding', tip: 'shares', kind: 'custom', value: isNum(c.shareOutstanding) ? compact(c.shareOutstanding * 1e6) : DASH },
                { label: 'P/E', tip: 'pe', kind: 'num', raw: m.peTTM },
                { label: 'P/S', tip: 'ps', kind: 'num', raw: m.psTTM },
                { label: 'P/B', tip: 'pb', kind: 'num', raw: m.pbQuarterly },
                // Price to cash per share (not price to cash flow).
                { label: 'P/C', tip: 'pc', kind: 'num', raw: priceToCash },
                { label: 'P/FCF', tip: 'pfcf', kind: 'num', raw: m.pfcfShareTTM },
                { label: 'Book/share QTR', tip: 'book', kind: 'money', raw: m.bookValuePerShareQuarterly },
                { label: 'Cash/share QTR', tip: 'cash', kind: 'money', raw: m.cashPerSharePerShareQuarterly },

                // Earnings & growth
                { label: 'EPS (TTM)', tip: 'eps-ttm', kind: 'money', raw: m.epsTTM },
                { label: 'EPS this Year', tip: 'eps-year', kind: 'money', raw: m.epsAnnual },
                { label: 'EPS Q/Q', tip: 'eps-qq', kind: 'pctTone', raw: m.epsGrowthQuarterlyYoy },
                { label: 'EPS Y/Y TTM', tip: 'eps-yy', kind: 'pctTone', raw: m.epsGrowthTTMYoy },
                { label: 'Sales Q/Q', tip: 'sales-qq', kind: 'pctTone', raw: m.revenueGrowthQuarterlyYoy },
                { label: 'Sales Y/Y TTM', tip: 'sales-yy', kind: 'pctTone', raw: m.revenueGrowthTTMYoy },
                { label: 'Sales past 5Y', tip: 'sales-5y', kind: 'pctTone', raw: m.revenueGrowth5Y },

                // Profitability & balance sheet
                { label: 'Gross Margin', tip: 'gross', kind: 'pct', raw: m.grossMarginTTM },
                { label: 'Operating Margin', tip: 'operating', kind: 'pctTone', raw: m.operatingMarginTTM },
                { label: 'Profit Margin', tip: 'profit', kind: 'pctTone', raw: m.netProfitMarginTTM },
                { label: 'ROA', tip: 'roa', kind: 'pctTone', raw: m.roaTTM },
                { label: 'ROE', tip: 'roe', kind: 'pctTone', raw: m.roeTTM },
                { label: 'ROI', tip: 'roi', kind: 'pctTone', raw: m.roiTTM },
                { label: 'Current Ratio', tip: 'current', kind: 'num', raw: m.currentRatioQuarterly },
                { label: 'Quick Ratio', tip: 'quick', kind: 'num', raw: m.quickRatioQuarterly },
                { label: 'Debt/Equity', tip: 'de', kind: 'num', raw: m.totalDebtToTotalEquityQuarterly },
                { label: 'LT Debt/Equity', tip: 'ltde', kind: 'num', raw: m.longTermDebtEquityQuarterly },
            ]

            return list.map((row) => {
                if (row.kind === 'custom' || row.kind === 'bool') return row
                return { ...row, value: FORMAT[row.kind](row.raw) }
            })
        },
    },
    methods: {
        toneClass(row) {
            if (!['pctTone', 'moneyTone'].includes(row.kind)) return 'text-ink'
            const t = tone(row.raw)
            if (t === 'up') return 'text-up'
            if (t === 'down') return 'text-down'
            return 'text-ink'
        },
    },
}
</script>
